import React, { createContext, useContext, ReactNode } from 'react';

type Language = 'en';

interface LanguageContextType {
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.features': 'Features',
    'nav.modules': 'Modules',
    'nav.company': 'Company',
    'nav.module.management': 'Management Center',
    'nav.module.status': 'Status & Report',
    'nav.module.mobile': 'Mobile',
    'nav.module.production': 'Production',
    'nav.benefits': 'Benefits',
    'nav.technology': 'Technology',
    'nav.contact': 'Contact',

    // Hero Section
    'hero.title.main': 'Waymining FMS: Complete',
    'hero.title.highlight': 'Mining Operations Control',
    'hero.title.subtitle': 'System',
    'hero.description': 'Operational control and equipment management software for mining operations with real-time monitoring, comprehensive reporting, and intelligent fleet coordination.',
    'hero.button.start': 'Get Started',
    'hero.button.demo': 'Export PDF',
    'hero.stat.automatic': 'Automatic',
    'hero.stat.automatic.sub': 'Assignment System',
    'hero.stat.realtime': 'Real-time',
    'hero.stat.realtime.sub': 'Fleet Management',
    'hero.stat.production': '24/7',
    'hero.stat.production.sub': 'Production Tracking',

    // Features Section - 6 Official Modules
    'features.title': 'Waymining FMS System Modules',
    'features.description': 'Complete mining operations management through integrated modules providing operational control, status monitoring, mobile coordination, production intelligence, predictive analytics, and contract management.',
    'features.gallery.title': 'System Module Interfaces',
    'features.module1.title': 'Waymining FMS Management Center',
    'features.module1.description': 'Complete system configuration and operational structure setup - Operator registration, shift management, fleet setup, operational activities, and maintenance scheduling',
    'features.module2.title': 'Waymining FMS Status and Report',
    'features.module2.description': 'Equipment status monitoring and comprehensive reporting - Physical availability, utilization metrics, MTBF analysis, and operational dashboards',
    'features.module3.title': 'Waymining FMS Mobile',
    'features.module3.description': 'Embedded tablet interface for field equipment - Real-time data collection, GPS tracking, offline functionality with store-and-forward synchronization',
    'features.module4.title': 'Waymining FMS Production',
    'features.module4.description': 'Advanced production cycle management - Truck-excavator cycle tracking, automatic assignment system, KPI monitoring, and queue management',
    'features.module5.title': 'Waymining FMS AI Analytics',
    'features.module5.description': 'Predictive intelligence for fleet and production - Machine learning models forecast maintenance needs, bottlenecks, and production outcomes from live operational data',
    'features.module6.title': 'Waymining FMS Contract Management',
    'features.module6.description': 'Multi-company subcontract administration - Track mining subcontractor agreements, equipment allocation, billing terms, and performance across every contracted operation',

    // Contact Section
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'Ready to transform your mining operations? Contact us today.',
    'contact.form.title': 'Send us a message',
    'contact.form.name': 'Full Name',
    'contact.form.name.placeholder': 'John Doe',
    'contact.form.email': 'Email Address',
    'contact.form.email.placeholder': 'john@company.com',
    'contact.form.company': 'Company',
    'contact.form.company.placeholder': 'Mining Corp',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell us about your needs...',
    'contact.form.submit': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.phone': 'Phone',
    'contact.info.email': 'Email',
    'contact.info.office': 'Office',
    'contact.info.phone.value': 'Contact for details',
    'contact.hours.title': 'Business Hours',
    'contact.hours.weekdays': 'Monday - Friday: 8:00 AM - 6:00 PM',
    'contact.hours.saturday': 'Saturday: 9:00 AM - 4:00 PM',
    'contact.hours.sunday': 'Sunday: Emergency Support Only',
    'contact.follow.title': 'Follow Us',
    'contact.footer.privacy': 'Privacy Policy',
    'contact.footer.terms': 'Terms of Service',
    'contact.footer.cookies': 'Cookie Policy',
    'contact.footer.sitemap': 'Sitemap',
    'contact.footer.copyright': `© ${new Date().getFullYear()} Waymining. All rights reserved.`,

    // Benefits Section (replacing Statistics)
    'benefits.title': 'Waymining FMS System Benefits',
    'benefits.description': 'Experience the transformational impact of comprehensive mining operations management with real-time monitoring, cost reduction, operational efficiency, and continuous improvement.',
    'benefits.realtime.title': 'Real-time Monitoring',
    'benefits.cost.title': 'Cost Reduction',
    'benefits.efficiency.title': 'Operational Efficiency',
    'benefits.improvement.title': 'Continuous Improvement',
    'benefits.impact.title': 'Why Choose Waymining FMS?',
    'benefits.impact.security.icon': '🛡️',
    'benefits.impact.security.text': 'Security Standards Compliance',
    'benefits.impact.assets.icon': '📊',
    'benefits.impact.assets.text': 'Effective Asset Management',
    'benefits.impact.decisions.icon': '🎯',
    'benefits.impact.decisions.text': 'Informed Decision Making',

    // Infrastructure Section (replacing Technology)
    'infrastructure.title': 'Cloud-Based Platform Technology',
    'infrastructure.description': 'Enterprise-grade Azure cloud platform with robust security, offline capabilities for mobile network issues, and PostgreSQL database for reliable mining operations management.',
    'infrastructure.hardware.title': 'Azure Cloud Platform',
    'infrastructure.hardware.description': 'Enterprise-grade cloud infrastructure with 99.9% uptime',
    'infrastructure.hardware.category': 'Cloud',
    'infrastructure.server.title': 'Security & Compliance',
    'infrastructure.server.description': 'End-to-end encryption, secure authentication, and data protection',
    'infrastructure.server.category': 'Security',
    'infrastructure.communication.title': 'Offline Capabilities',
    'infrastructure.communication.description': 'Mobile apps work offline during network issues with auto-sync',
    'infrastructure.communication.category': 'Resilience',
    'infrastructure.mobile.title': 'PostgreSQL Database',
    'infrastructure.mobile.description': 'High-performance database with automatic backups and scaling',
    'infrastructure.mobile.category': 'Database',
    'infrastructure.onpremise.title': 'On-Premise Deployment',
    'infrastructure.onpremise.description': 'Complete local deployment option for enhanced security and control',
    'infrastructure.onpremise.category': 'Deployment',
    'infrastructure.ai.title': 'AI Integration Ready',
    'infrastructure.ai.description': 'Advanced analytics, KPI optimization, and dynamic assignment algorithms',
    'infrastructure.ai.category': 'AI/ML',
    'infrastructure.platform.title': 'Azure Cloud Excellence',
    'infrastructure.platform.description': 'Built on Microsoft Azure cloud platform providing enterprise-grade reliability, security, and scalability for mining operations worldwide.',
    'infrastructure.platform.feature1': '☁️ Azure Infrastructure: Scalable cloud platform with global reach',
    'infrastructure.platform.feature2': '🛡️ Enterprise Security: Multi-layer security with Azure Active Directory',
    'infrastructure.platform.feature3': '📱 Offline Resilience: Mobile apps continue working during network outages',
    'infrastructure.platform.feature4': '🗄️ PostgreSQL Database: Reliable data storage with automatic backups',
    'infrastructure.capabilities.title': 'Technology Capabilities',
    'infrastructure.capabilities.security': '🔐 Azure Security Center Integration',
    'infrastructure.capabilities.assets': '☁️ Cloud-Native Architecture',
    'infrastructure.capabilities.decisions': '📡 Offline-First Mobile Design',
    'infrastructure.capabilities.integration': '🗄️ PostgreSQL Enterprise Database',

    // Module Details — Fleet Management Platform deep-dive
    'modules.management.features.title': 'Key Features',
    'modules.management.features.operators': 'Operator Registration and Management',
    'modules.management.features.shifts': 'Shift and Crew Management',
    'modules.management.features.fleets': 'Fleet Configuration and Setup',
    'modules.management.features.activities': 'Equipment Operational Activities',
    'modules.management.features.maintenance': 'Maintenance Scheduling and Tracking',
    'modules.management.features.operations': 'Operational Stop Reasons',

    'modules.status.features.title': 'Monitoring Capabilities',
    'modules.status.features.equipment': 'Real-time Equipment Status',
    'modules.status.features.availability': 'Physical Availability Metrics',
    'modules.status.features.utilization': 'Equipment Utilization Analysis',
    'modules.status.features.mtbf': 'MTBF (Mean Time Between Failures)',
    'modules.status.features.worked': 'Worked Hours by Activity Measurement',
    'modules.status.features.operational': 'Operational Stop Time Measurement',
    'modules.status.features.maintenance': 'Maintenance Stop Time Measurement',

    'modules.mobile.features.title': 'Mobile Capabilities',
    'modules.mobile.features.integrated': 'Integrated with all Waymining FMS modules',
    'modules.mobile.features.interaction': 'Interactive configuration parameter modification',
    'modules.mobile.features.realtime': 'Real-time parameter updates',
    'modules.mobile.features.offline': 'Store and forward system (offline capable)',
    'modules.mobile.features.gps': 'GPS location tracking and coordination',
    'modules.mobile.features.intuitive': 'Intuitive interface for field operations',

    'modules.production.features.title': 'Production Intelligence',
    'modules.production.features.cycle': 'Complete truck-excavator cycle tracking',
    'modules.production.features.assignment': 'Automatic equipment assignment system',
    'modules.production.features.kpis': 'Real-time KPI monitoring and calculation',
    'modules.production.features.queue': 'FIFO queue management with manual override',
    'modules.production.features.tracking': 'Time and distance tracking for all cycles',
    'modules.production.features.realtime': 'Live production dashboard updates',

    'modules.aianalytics.features.title': 'Predictive Capabilities',
    'modules.aianalytics.features.maintenance': 'Predictive maintenance alerts before failures occur',
    'modules.aianalytics.features.bottleneck': 'Bottleneck detection across the production cycle',
    'modules.aianalytics.features.forecast': 'Production and utilization forecasting',
    'modules.aianalytics.features.anomaly': 'Anomaly detection on equipment and operator behavior',
    'modules.aianalytics.features.recommendation': 'AI-generated operational recommendations',
    'modules.aianalytics.features.learning': 'Continuous learning from historical operational data',

    'modules.contract.features.title': 'Contract Capabilities',
    'modules.contract.features.subcontractors': 'Multi-company subcontractor registration and management',
    'modules.contract.features.equipment': 'Equipment allocation tracking per contract',
    'modules.contract.features.billing': 'Billing terms, rates, and invoicing tracking',
    'modules.contract.features.performance': 'Subcontractor performance and compliance monitoring',
    'modules.contract.features.expiry': 'Contract expiry and renewal alerts',
    'modules.contract.features.reporting': 'Consolidated reporting across all mining subcontracts',

    'modules.production.gallery.title': 'Production Interfaces',
    'modules.production.flow.title': 'Production Cycle States',
    'modules.production.flow.empty': 'Empty Travel',
    'modules.production.flow.empty.desc': 'Truck traveling empty to excavator location',
    'modules.production.flow.loading': 'Loading',
    'modules.production.flow.loading.desc': 'Truck being loaded by excavator (queue management)',
    'modules.production.flow.full': 'Full Travel',
    'modules.production.flow.full.desc': 'Truck traveling full to assigned dump location',
    'modules.production.flow.dumping': 'Dumping',
    'modules.production.flow.dumping.desc': 'Truck dumping at location and completing cycle',
    'modules.production.excavator.title': 'Excavator Control Interface',
    'modules.production.excavator.desc': 'Control Room Actions: "Start Loading" when truck arrives, "Full" when loading complete. System tracks excavator hang time and manages FIFO queue with manual override capability.',
    'modules.production.truck.title': 'Truck Action Interface',
    'modules.production.truck.desc': 'Control Room Actions: "Arrival" at excavator/dump locations, "Complete" when dumping finished. Tracks travel times, queue positions, and automatically assigns dump locations.',
    'modules.production.kpi.title': 'Complete Production KPI Monitoring',
    'modules.production.kpi.emphasis': '🎯 Waymining FMS manages the entire production cycle automatically - from truck assignment to cycle completion with comprehensive KPI tracking',
    'modules.production.kpi.truck.title': 'Truck Performance KPIs',
    'modules.production.kpi.operations.title': 'Operational Efficiency KPIs',
    'modules.production.kpi.analytics.title': 'Production Analytics',
    'modules.production.kpi.empty.travel': 'Empty Travel Time',
    'modules.production.kpi.empty.travel.desc': 'Time from dispatch to excavator arrival',
    'modules.production.kpi.full.travel': 'Full Travel Time',
    'modules.production.kpi.full.travel.desc': 'Time from loading completion to dump arrival',
    'modules.production.kpi.empty.distance': 'Empty Distance',
    'modules.production.kpi.empty.distance.desc': 'Distance traveled empty to excavator',
    'modules.production.kpi.full.distance': 'Full Distance',
    'modules.production.kpi.full.distance.desc': 'Distance traveled full to dump location',
    'modules.production.kpi.cycle.time': 'Complete Cycle Time',
    'modules.production.kpi.cycle.time.desc': 'Total time from assignment to completion',
    'modules.production.kpi.spot.time': 'Spot Time',
    'modules.production.kpi.spot.time.desc': 'Wait time from arrival to loading start',
    'modules.production.kpi.queue.time': 'Queue Time',
    'modules.production.kpi.queue.time.desc': 'Loading and dumping queue delays',
    'modules.production.kpi.loading.time': 'Loading Time',
    'modules.production.kpi.loading.time.desc': 'Active excavator loading duration',
    'modules.production.kpi.dumping.time': 'Dumping Time',
    'modules.production.kpi.dumping.time.desc': 'Active dumping operation duration',
    'modules.production.kpi.hang.time': 'Excavator Hang Time',
    'modules.production.kpi.hang.time.desc': 'Time excavator waits for truck arrival',
    'modules.production.kpi.total.production': 'Total Production',
    'modules.production.kpi.total.production.desc': 'Daily/shift material movement totals',
    'modules.production.kpi.equipment.utilization': 'Equipment Utilization',
    'modules.production.kpi.equipment.utilization.desc': 'Truck and excavator efficiency rates',
    'modules.production.kpi.cycle.efficiency': 'Cycle Efficiency',
    'modules.production.kpi.cycle.efficiency.desc': 'Overall production cycle optimization',
    'modules.production.kpi.fleet.performance': 'Fleet Performance',
    'modules.production.kpi.fleet.performance.desc': 'Comparative analysis across equipment',
    'modules.production.kpi.realtime.monitoring': 'Real-time Monitoring',
    'modules.production.kpi.realtime.monitoring.desc': 'Live dashboard updates and alerts'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const language: Language = 'en';

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
